import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Eye,
  FileText,
  Globe,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Phone,
  SearchIcon,
  Trash2,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import * as React from 'react';

import {
  BulkDeleteDialog,
  BulkSelectionToolbar,
  useBulkDelete,
} from '@/components/app/bulk-delete';
import { ListPage, ListPagination } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { PageTabBar } from '@/components/app/page-tab-bar';
import { FacebookLogo, InstagramLogo } from '@/components/global/brand-icons';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ImportDocumentsDialog } from '@/features/document-imports';
import {
  ImportLeadsCsvDialog,
  type LeadListItem,
  useDeleteLead,
  useListLeads,
} from '@/features/leads';
import { useIsMobile } from '@/hooks/use-mobile';
import { useResolvedRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import { leadStatusLabels } from '@borradh-workspace/api-client/types';
import type { LeadSourceTab, LeadStageTab } from '@borradh-workspace/labels';
import {
  leadSourceTabLabels,
  leadSourceTabOf,
  leadSourceTabValues,
} from '@borradh-workspace/labels';

import { useLeadStageCounts } from '../api';
import {
  ALWAYS_SHOWN_SOURCE_TABS,
  DEFAULT_SOURCE_VIEW,
  HIDDEN_SOURCE_TABS,
  type LeadSourceView,
  STAGE_GROUP_TABS,
} from '../constants';
import { WaitingForTeamBadge } from './waiting-for-team-badge';

const stageStyles: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  contacted: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  qualified:
    'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  booked:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  lost: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
};

const avatarColors = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
  'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IE', { month: 'short', day: 'numeric' });
}

const sourceIcons: Record<
  LeadSourceTab,
  React.ComponentType<{ className?: string }>
> = {
  meta_ads: FileText,
  instagram: InstagramLogo,
  whatsapp: MessageCircle,
  facebook: FacebookLogo,
  sms: MessageSquare,
  phone: Phone,
  website: Globe,
  referral: Users,
  team: UserPlus,
  imported: Upload,
  other: Globe,
};

interface CustomersPageProps {
  /** Active tab; owned by the route via search params so it survives reload. */
  tab: LeadStageTab;
  onTabChange: (tab: LeadStageTab) => void;
  /**
   * Leads-tab source view, URL-owned too. `undefined` is the default view
   * (`DEFAULT_SOURCE_VIEW`, Lead forms); `all` is every lead.
   */
  sourceTab?: LeadSourceView;
  onSourceTabChange: (source: LeadSourceView) => void;
  /** Zero-based page index; persisted in URL search params. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function CustomersPage({
  tab,
  onTabChange,
  sourceTab: sourceTabParam,
  onSourceTabChange,
  page: pageIndex,
  pageSize,
  onPageChange: setPageIndex,
  onPageSizeChange,
}: CustomersPageProps) {
  const sourceTab: LeadSourceView = sourceTabParam ?? DEFAULT_SOURCE_VIEW;
  const { deleteLead, isDeleting } = useDeleteLead();
  // A second, quiet instance for bulk delete: it raises one summary toast
  // instead of one per client.
  const { deleteLeadAsync: deleteLeadQuietly } = useDeleteLead({
    showSuccessToast: false,
    showErrorToast: false,
  });
  const { counts } = useLeadStageCounts();
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const isMobile = useIsMobile();

  const [importOpen, setImportOpen] = React.useState<
    'leads' | 'documents' | null
  >(null);
  const [hiddenColumns] = React.useState<Set<string>>(() => new Set());
  const [search, setSearch] = React.useState('');

  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const prevFilters = React.useRef({ tab, debouncedSearch });
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetting on the inputs is the point
  React.useEffect(() => {
    const prev = prevFilters.current;
    prevFilters.current = { tab, debouncedSearch };
    if (prev.tab === tab && prev.debouncedSearch === debouncedSearch) {
      return;
    }
    setPageIndex(0);
  }, [tab, debouncedSearch]);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleTabChange = React.useCallback(
    (next: LeadStageTab) => {
      onTabChange(next);
    },
    [onTabChange]
  );

  const filters = React.useMemo(
    () => ({
      stageGroup: tab,
      // A search covers the whole view, whatever source tab is open: someone
      // typing a name on the default Lead forms view means "find this
      // person", not "find them if they came from a lead form".
      sourceTab:
        tab === 'leads' && sourceTab !== 'all' && !debouncedSearch.trim()
          ? sourceTab
          : undefined,
      search: debouncedSearch.trim() || undefined,
      // Newest first on every view (who was added most recently).
      sort: 'recent' as const,
      limit: pageSize,
      offset: pageIndex * pageSize,
    }),
    [tab, sourceTab, debouncedSearch, pageIndex, pageSize]
  );

  const { leads, total, isLoading, isError, refetch } = useListLeads({
    filters,
    queryConfig: { placeholderData: keepPreviousData },
  });

  const handleCreate = React.useCallback(() => {
    void navigate({ params: { entity: 'customer' }, to: '/create/$entity' });
  }, [navigate]);

  const handleViewDetails = React.useCallback(
    (leadId: string) => {
      void navigate({
        to: routes.customerDetail(leadId),
      });
    },
    [navigate, routes.customerDetail]
  );

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);

  React.useEffect(() => {
    if (!isLoading && pageIndex !== safePageIndex) {
      setPageIndex(safePageIndex);
    }
  }, [isLoading, pageIndex, safePageIndex, setPageIndex]);

  // -- Selection + bulk delete --
  const leadIds = React.useMemo(() => leads.map((lead) => lead.id), [leads]);
  const bulk = useBulkDelete({
    deleteOne: deleteLeadQuietly,
    noun: { singular: 'customer', plural: 'customers' },
    rowIds: leadIds,
    labelFor: (id) => {
      const lead = leads.find((l) => l.id === id);
      return lead
        ? `${lead.firstName} ${lead.lastName || ''}`.trim() || undefined
        : undefined;
    },
    // Clear the selection when the view changes (tab, source, search, page).
    resetKey: `${tab}|${sourceTab}|${debouncedSearch}|${pageIndex}`,
  });

  const columns: ListColumn<LeadListItem>[] = React.useMemo(() => {
    const all: ListColumn<LeadListItem>[] = [
      {
        id: 'name',
        header: 'Name',
        mobile: 'primary',
        cell: (lead) => {
          const fullName = `${lead.firstName} ${lead.lastName || ''}`.trim();
          const initials =
            `${lead.firstName.charAt(0)}${lead.lastName?.charAt(0) || ''}`.toUpperCase();
          return (
            <span className="flex min-w-0 items-center gap-3">
              <Avatar className="size-8 shrink-0">
                <AvatarFallback
                  className={cn(
                    'font-semibold text-xs',
                    avatarColor(fullName || 'Client')
                  )}
                >
                  {initials || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {fullName || 'Client'}
                </span>
                {lead.email && (
                  <span className="hidden truncate text-muted-foreground text-xs md:block">
                    {lead.email}
                  </span>
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        mobile: 'trailing',
        cell: (lead) => (
          <span className="flex flex-wrap items-center gap-1.5">
            {/* On a phone the row has room for one badge: "Waiting for you"
                already says the lead is new. */}
            <Badge
              className={cn(
                'border-transparent font-medium',
                stageStyles[lead.stage],
                lead.waitingForTeamSince && 'hidden md:inline-flex'
              )}
            >
              {leadStatusLabels[lead.stage]}
            </Badge>
            {lead.waitingForTeamSince && (
              <WaitingForTeamBadge since={lead.waitingForTeamSince} />
            )}
          </span>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: (lead) => {
          // The bucket the source tabs count this row under, so an imported
          // row reads "Imported", not "Manual".
          const bucket = leadSourceTabOf(lead.source, lead.consentSource);
          const SourceIcon = sourceIcons[bucket];
          return (
            <Badge
              className="gap-1.5 px-2 py-1 text-muted-foreground"
              variant="outline"
            >
              <SourceIcon className="size-3" />
              {leadSourceTabLabels[bucket]}
            </Badge>
          );
        },
      },
      {
        id: 'lastVisit',
        header: 'Last visit',
        cell: (lead) => (
          <span className="text-muted-foreground text-sm">
            {lead.lastVisitAt ? formatShortDate(lead.lastVisitAt) : '—'}
          </span>
        ),
      },
      {
        id: 'phone',
        header: 'Phone',
        mobile: 'secondary',
        cell: (lead) => (
          <span className="text-muted-foreground text-sm tabular-nums">
            {lead.phone || '—'}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Date added',
        cell: (lead) => (
          <span className="text-muted-foreground text-sm">
            {formatShortDate(lead.createdAt)}
          </span>
        ),
      },
    ];

    return all.map((column) => ({
      ...column,
      hidden: hiddenColumns.has(column.id),
    }));
  }, [hiddenColumns]);

  // The same full-width view bar as the Inbox (`PageTabBar`).
  const stageTabs = (
    <PageTabBar
      label="Customer views"
      onChange={handleTabChange}
      tabs={STAGE_GROUP_TABS.map((t) => ({
        ...t,
        count: counts?.tabs[t.value],
      }))}
      value={tab}
    />
  );

  // Leads tab only: where each unconverted lead came from. Lead forms first
  // (and open by default), "All leads" last. A source with no leads is hidden
  // (most clinics use three or four of the ten) unless it is always shown or
  // the one selected, so a saved link never lands on a vanished tab. Referral
  // and Added by team never get a tab (`HIDDEN_SOURCE_TABS`); those leads are
  // under All leads.
  const sourceOptions: { value: LeadSourceView; label: string }[] = [
    ...leadSourceTabValues
      .filter((value) => !HIDDEN_SOURCE_TABS.has(value))
      .filter(
        (value) =>
          ALWAYS_SHOWN_SOURCE_TABS.has(value) ||
          (counts?.leadSources?.[value] ?? 0) > 0 ||
          value === sourceTab
      )
      .map((value) => ({ value, label: leadSourceTabLabels[value] })),
    { value: 'all', label: 'All leads' },
  ];

  const searchField = (
    <div
      className={cn(
        'relative w-full shrink-0 md:w-56',
        tab === 'leads' && 'md:mb-1.5'
      )}
    >
      <SearchIcon className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-muted-foreground" />
      <Input
        aria-label={tab === 'leads' ? 'Search leads' : 'Search customers'}
        className="rounded-full pl-9"
        onChange={(event) => handleSearchChange(event.target.value)}
        placeholder={tab === 'leads' ? 'Search leads' : 'Search customers'}
        value={search}
      />
    </div>
  );

  // The row under the view bar: on Leads, the source tabs with the search at
  // their right end; on the other views (no sources), the search alone.
  const listControls =
    tab === 'leads' ? (
      <div className="flex flex-col-reverse gap-2 md:flex-row md:items-end md:gap-4 md:border-b">
        <div className="min-w-0 flex-1 overflow-x-auto border-b [scrollbar-width:none] md:border-b-0 [&::-webkit-scrollbar]:hidden">
          <div
            aria-label="Lead source"
            className="flex w-max gap-5"
            role="tablist"
          >
            {sourceOptions.map((option) => {
              // While searching no source tab is applied, so none reads as
              // selected (see `filters`).
              const isActive =
                !debouncedSearch.trim() && option.value === sourceTab;
              const count =
                option.value === 'all'
                  ? counts?.tabs.leads
                  : counts?.leadSources?.[option.value];
              return (
                <button
                  aria-selected={isActive}
                  className={cn(
                    '-mb-px inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    isActive
                      ? 'border-primary font-semibold text-foreground'
                      : 'border-transparent font-medium text-muted-foreground hover:text-foreground'
                  )}
                  key={option.label}
                  onClick={() => onSourceTabChange(option.value)}
                  role="tab"
                  type="button"
                >
                  {option.label}
                  {count !== undefined && (
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {/* Phone: the search is the list's own, beside the round + (below). */}
        {!isMobile && searchField}
      </div>
    ) : isMobile ? null : (
      searchField
    );

  const hasSelection = bulk.hasSelection;

  const toolbar = (
    <div
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-2',
        hasSelection && 'flex-1 justify-end'
      )}
    >
      {hasSelection && <BulkSelectionToolbar bulk={bulk} />}

      <ImportLeadsCsvDialog
        onOpenChange={(next) => setImportOpen(next ? 'leads' : null)}
        open={importOpen === 'leads'}
        trigger={null}
      />
      <ImportDocumentsDialog
        onOpenChange={(next) => setImportOpen(next ? 'documents' : null)}
        open={importOpen === 'documents'}
        trigger={null}
      />
    </div>
  );

  const footer = (
    <ListPagination
      label="clients"
      onPageChange={setPageIndex}
      onPageSizeChange={(size) => {
        onPageSizeChange(size);
      }}
      pageIndex={safePageIndex}
      pageSize={pageSize}
      total={total}
    />
  );

  return (
    <>
      <ListPage<LeadListItem>
        config={{
          title: 'Customers',
          columns,
          rows: leads,
          rowKey: (lead) => lead.id,
          rowTestId: (lead) => `mobile-customer-row-${lead.id}`,
          onRowClick: (lead) => handleViewDetails(lead.id),
          rowActions: (lead) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                  size="icon"
                  variant="ghost"
                >
                  <MoreVertical />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => handleViewDetails(lead.id)}>
                  <Eye className="size-4" />
                  View profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={isDeleting}
                  onClick={() => deleteLead(lead.id)}
                  variant="destructive"
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
          // Two rows. Desktop: the view bar with Add on the right, then the
          // source tabs with the search at their right end. Phone: the list's
          // own search field with the round + beside it (the phone pattern
          // every list uses), then the view bar and the source tabs.
          ...(isMobile
            ? {
                searchPlaceholder:
                  tab === 'leads' ? 'Search leads' : 'Search customers',
                search,
                onSearchChange: handleSearchChange,
              }
            : {}),
          searchLeading: stageTabs,
          belowToolbar: (
            <>
              {isMobile && stageTabs}
              {listControls}
            </>
          ),
          toolbar,
          primaryAction: {
            label: 'Add',
            ariaLabel: 'Add customer',
            onClick: handleCreate,
            // The ⋯ side button: adding one by hand is the button; the
            // imports are the other ways people get onto this list.
            menu: {
              trigger: 'more',
              items: [
                { label: 'Add customer', onSelect: handleCreate },
                {
                  label: 'Import customers',
                  onSelect: () => setImportOpen('leads'),
                },
                {
                  label: 'Import documents',
                  onSelect: () => setImportOpen('documents'),
                },
              ],
            },
          },
          footer,
          isLoading,
          isError,
          errorMessage: 'Failed to load clients. Please try again.',
          onRetry: () => void refetch(),
          empty: {
            icon: Users,
            title: search ? 'No matches' : 'Nothing to see here',
            description: search
              ? 'No clients match your search.'
              : 'No clients match this view yet.',
          },
          selection: bulk.selection,
        }}
      />

      <BulkDeleteDialog bulk={bulk} />
    </>
  );
}
